import { Skeleton } from 'moti/skeleton';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Text, View, StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import {
  InteractiveBox,
  InteractiveForwardRefProps,
} from '@/components/interactive';

export type ContactInfo = {
  name: string;
  email: string;
};

const SkeletonCommonProps = {
  colorMode: 'dark',
  transition: {
    type: 'timing',
    duration: 1000,
  },
} as const;

const ContactListSkeleton = () => {
  return (
    <View style={styles.container}>
      <Skeleton.Group show={true}>
        <Skeleton
          radius={'round'}
          height={50}
          width={50}
          {...SkeletonCommonProps}
        />
        <View style={styles.content}>
          <Skeleton height={20} width={180} {...SkeletonCommonProps} />
          <View style={styles.spacer} />
          <Skeleton height={15} width={160} {...SkeletonCommonProps} />
        </View>
      </Skeleton.Group>
    </View>
  );
};

type ContactListItemProps = {
  contact?: ContactInfo | null;
};

const ContactListItem: React.FC<ContactListItemProps> = ({ contact }) => {
  const isLoading = contact == null;

  if (isLoading) {
    return <ContactListSkeleton />;
  }

  return (
    <Animated.View style={styles.container} entering={FadeIn.duration(1000)}>
      <View style={[styles.circleContainer, styles.avatar]}>
        <Text style={styles.avatarText}>{contact.name?.[0]}</Text>
      </View>
      <View style={styles.content}>
        <Animated.Text entering={FadeIn.duration(1500)} style={styles.nameText}>
          {contact.name}
        </Animated.Text>
        <View style={styles.spacer} />
        <Animated.Text
          entering={FadeIn.duration(1500)}
          style={styles.emailText}
        >
          {contact.email}
        </Animated.Text>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 320,
    height: 90,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: '#ffffff37',
    backgroundColor: '#ffffff04',
    borderRadius: 12,
    borderCurve: 'continuous',
  },
  avatar: {
    height: 50,
    width: 50,
    borderRadius: 35,
  },
  content: {
    marginLeft: 15,
  },
  circleContainer: {
    backgroundColor: '#005CB7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 25,
    color: 'white',
  },
  nameText: {
    fontSize: 18,
    color: '#ffffff',
  },
  emailText: {
    fontSize: 15,
    color: '#d4d4d4',
  },
  spacer: {
    height: 5,
  },
});

const DEFAULT_CONTACT = {
  name: 'John Doe',
  email: 'john.doe@example.com',
};

const ContactItemContainer = forwardRef<InteractiveForwardRefProps>(
  (_, ref) => {
    const [contact, setContact] = useState<ContactInfo | null>(DEFAULT_CONTACT);

    const fetchContacts = useCallback(async (delay?: number) => {
      setContact(null);

      // wait for 2000ms to simulate loading
      await new Promise((resolve) => setTimeout(resolve, delay ?? 0));

      setContact(DEFAULT_CONTACT);
    }, []);

    useImperativeHandle(ref, () => ({
      reset: () => {},
      play: () => {
        fetchContacts(2000);
      },
      pause: () => {},
    }));

    return (
      <View style={interactiveContainerStyle}>
        <ContactListItem contact={contact} />
      </View>
    );
  }
);

export const InteractiveSkeletonDemo = () => {
  return <InteractiveBox children={ContactItemContainer} withPlay />;
};

const JustSkeleton = () => {
  return (
    <View style={interactiveContainerStyle}>
      <ContactListSkeleton />
    </View>
  );
};

export const InteractiveJustSkeleton = () => {
  return <InteractiveBox children={JustSkeleton} />;
};

export const InteractiveContactItem = () => {
  return <InteractiveBox children={ContactItemContainer} />;
};

const interactiveContainerStyle = {
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
} as const;
